import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchPresentationSettings } from './api';
import { factoryPresentationSettings, PresentationSettingsState } from './presentationSettings';

interface PresentationSettingsContextValue {
  settings: PresentationSettingsState;
  factory: PresentationSettingsState;
  reload: () => Promise<void>;
}

const PresentationSettingsContext = createContext<PresentationSettingsContextValue>({
  settings: factoryPresentationSettings(),
  factory: factoryPresentationSettings(),
  reload: async () => undefined,
});

export const PresentationSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const localFactory = useMemo(() => factoryPresentationSettings(), []);
  const [settings, setSettings] = useState<PresentationSettingsState>(localFactory);
  const [factory, setFactory] = useState<PresentationSettingsState>(localFactory);

  const reload = useCallback(async () => {
    try {
      const data = await fetchPresentationSettings();
      setSettings({
        ...localFactory,
        ...data.settings,
        prompts: data.settings.prompts || data.factory?.prompts || localFactory.prompts,
        colors: data.settings.colors?.length ? data.settings.colors : localFactory.colors,
        tableDefaults: { ...localFactory.tableDefaults, ...data.settings.tableDefaults },
        chartDefaults: { ...localFactory.chartDefaults, ...data.settings.chartDefaults },
      });
      if (data.factory) setFactory(data.factory);
    } catch {
      setSettings(localFactory);
    }
  }, [localFactory]);

  useEffect(() => { void reload(); }, [reload]);

  const value = useMemo(() => ({ settings, factory, reload }), [settings, factory, reload]);
  return <PresentationSettingsContext.Provider value={value}>{children}</PresentationSettingsContext.Provider>;
};

export function usePresentationSettings(): PresentationSettingsContextValue {
  return useContext(PresentationSettingsContext);
}
